import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Target, TrendingUp, Users, Zap, Star, ArrowRight, Play, Shield, Clock, 
  Brain, Rocket, CheckCircle, MessageSquare, BarChart3, FileText, Calendar,
  DollarSign, Lightbulb, Award, Globe, Heart, Eye, Target as TargetIcon
} from 'lucide-react';

const About: React.FC = () => {
  const values = [
    {
      icon: Target,
      title: "Speed Over Perfection",
      description: "We believe in launching fast and iterating. Perfect is the enemy of profitable."
    },
    {
      icon: Heart,
      title: "Customer-First",
      description: "Every decision is made with the customer in mind. Validate, then build."
    },
    {
      icon: Zap,
      title: "Accountability",
      description: "Success comes from consistent daily action. We keep you on track."
    },
    {
      icon: Users,
      title: "Community",
      description: "You're not alone. Join fellow entrepreneurs on the same journey."
    }
  ];

  const team = [
    {
      name: "The StartupStreak Team",
      role: "Entrepreneurs & Builders",
      description: "We're a team of entrepreneurs who've been in your shoes. We've launched businesses, failed, learned, and succeeded. Now we're helping others do the same.",
      avatar: "30"
    }
  ];

  const milestones = [
    {
      year: "2024",
      title: "Platform Launch",
      description: "Launched the complete StartupStreak platform with AI validation and accountability tools."
    },
    {
      year: "2024",
      title: "First 100 Users",
      description: "Helped our first 100 entrepreneurs complete their sprints and make their first sales."
    },
    {
      year: "2025",
      title: "₹30L+ Revenue",
      description: "Our users collectively generated over ₹30 lakhs in revenue through their sprints."
    }
  ];

  function setShowDemo(arg0: boolean): void {
    throw new Error('Function not implemented.');
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-600 via-teal-500 to-orange-400 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/10" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-light mb-8 leading-tight">
              About
              <br />
              <span className="font-bold">StartupStreak</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-12 leading-relaxed">
              We're on a mission to help entrepreneurs go from idea to revenue in 30 days. 
              No excuses, no overthinking, just execution.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/signup">
                <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:from-yellow-300 hover:to-orange-400 text-lg px-12 py-4 font-semibold shadow-2xl hover:shadow-yellow-400/25 transition-all">
                  Join the Movement
                  
                </Button>
              </Link>
              
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-6xl font-light mb-12">Our Mission</h2>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed mb-12">
              To eliminate the gap between having a business idea and making your first sale. 
              We believe every entrepreneur deserves the tools, accountability, and community 
              to turn their vision into reality.
            </p>
            <div className="grid md:grid-cols-2 gap-8 mt-16">
              <div className="text-center">
                <div className="text-6xl font-light text-gray-300 mb-4">30</div>
                <div className="text-xl font-medium text-black">Days to Launch</div>
                <p className="text-gray-600 mt-2">That's our promise. No more endless planning.</p>
              </div>
              <div className="text-center">
                <div className="text-6xl font-light text-gray-300 mb-4">₹1L+</div>
                <div className="text-xl font-medium text-black">Revenue Goal</div>
                <p className="text-gray-600 mt-2">Your first paying customer is closer than you think.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-32 bg-gray-50 text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-6xl font-light mb-8">The Problem We Solve</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Most entrepreneurs get stuck in the planning phase. They overthink, overanalyze, 
                and never actually launch. We fix that.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg border border-gray-200">
                <div className="w-16 h-16 rounded-xl bg-red-100 flex items-center justify-center mb-6">
                  <Eye className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-medium text-black mb-4">Analysis Paralysis</h3>
                <p className="text-gray-600 leading-relaxed">
                  Entrepreneurs spend months researching and planning, but never actually start building.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg border border-gray-200">
                <div className="w-16 h-16 rounded-xl bg-yellow-100 flex items-center justify-center mb-6">
                  <TargetIcon className="w-8 h-8 text-yellow-600" />
                </div>
                <h3 className="text-xl font-medium text-black mb-4">Lack of Accountability</h3>
                <p className="text-gray-600 leading-relaxed">
                  Without deadlines and accountability, projects drag on indefinitely with no clear end goal.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg border border-gray-200">
                <div className="w-16 h-16 rounded-xl bg-blue-100 flex items-center justify-center mb-6">
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-medium text-black mb-4">Isolation</h3>
                <p className="text-gray-600 leading-relaxed">
                  Building alone is hard. Entrepreneurs need community, support, and shared experiences.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Solution Section */}
      <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-6xl font-light mb-8">Our Solution</h2>
              <p className="text-xl text-blue-200 max-w-3xl mx-auto">
                A complete 30-day sprint system that forces you to execute, not just plan.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-16">
              <div>
                <h3 className="text-3xl font-light mb-8">The 30-Day Sprint Method</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-medium text-sm">1</div>
                    <div>
                      <h4 className="text-xl font-medium text-white mb-2">Week 1: Validate</h4>
                      <p className="text-blue-200">Use AI to test your idea with real market feedback.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-medium text-sm">2</div>
                    <div>
                      <h4 className="text-xl font-medium text-white mb-2">Week 2-3: Build</h4>
                      <p className="text-blue-200">Create your MVP using proven playbooks and templates.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-medium text-sm">3</div>
                    <div>
                      <h4 className="text-xl font-medium text-white mb-2">Week 4: Launch</h4>
                      <p className="text-blue-200">Go to market and get your first paying customer.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="text-3xl font-light mb-8">What You Get</h3>
                <div className="space-y-4">
                  {[
                    "AI-powered idea validation with real investor feedback",
                    "Daily accountability from Cashpurr (your AI boss)",
                    "Proven launch playbooks and templates",
                    "ROI tracking to measure your progress",
                    "Community pods for peer support",
                    "30-day sprint plan with daily tasks"
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-teal-300 flex-shrink-0" />
                      <span className="text-blue-200">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-light mb-8">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-medium text-black mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-32 bg-gray-50 text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {team.map((member, index) => (
              <div key={index} className="bg-white p-8 rounded-lg border border-gray-200 text-center">
                <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-2xl font-bold text-gray-600 mx-auto mb-6">
                  {member.avatar}
                </div>
                <h3 className="text-2xl font-medium text-black mb-2">{member.name}</h3>
                <p className="text-gray-600 mb-4">{member.role}</p>
                <p className="text-gray-700 leading-relaxed max-w-2xl mx-auto">
                  {member.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones Section */}
      <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-light mb-8">Our Journey</h2>
            <p className="text-xl text-blue-200 max-w-2xl mx-auto">
              Key milestones in our mission to help entrepreneurs
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="flex items-center gap-8">
                <div className="text-4xl font-light text-teal-400/30 min-w-[100px]">{milestone.year}</div>
                <div className="flex-1">
                  <h3 className="text-2xl font-medium text-white mb-2">{milestone.title}</h3>
                  <p className="text-blue-200 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
              Ready to Join the Movement?
            </h2>
            <p className="text-xl text-blue-200 mb-12 leading-relaxed">
              Start your 30-day streak today and join hundreds of entrepreneurs 
              who've already made their first sale
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link to="/signup">
                <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-black text-lg px-12 py-4 font-semibold shadow-2xl hover:shadow-yellow-400/25 transition-all">
                  Start Your Streak Now
                </Button>
              </Link>
              <div className="flex items-center gap-2 text-blue-200">
                <Clock className="w-5 h-5" />
                <span>Free trial</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
